import { compact } from 'lodash'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import AssignmentIcon from '@mui/icons-material/Assignment'
import Biotech from '@mui/icons-material/Biotech'
import EmailIcon from '@mui/icons-material/Email'
import FolderSharedIcon from '@mui/icons-material/FolderShared'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import PaymentIcon from '@mui/icons-material/Payment'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'

import ConsultationDocumentsIcon from '@/components/ui/icons/ConsultationDocuments'
import MedicalVideoIcon from '@/components/ui/icons/MedicalVideo'
import PatientIdentityIcon from '@/components/ui/icons/PatientIdentity'
import StethoscopeIcon from '@/components/ui/icons/Stethoscope'
import VitalParametersIcon from '@/components/ui/icons/VitalParameters'
import { useGetConsultationQuery } from '@/core/services/consultation'
import { useShowDMP } from '@/features/dmp-connect'
import { useMedicalActsLicenseEnabled } from '@/features/medical-acts/model/useMedicalActsLicenseEnabled'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

export function useSidebarMenu(consultationId?: string) {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const isTooSmall = useIsMobile()
  const hasMedicalActsLicense = useMedicalActsLicenseEnabled()

  const { data: consultation } = useGetConsultationQuery(consultationId ?? skipToken)
  const showDMP = useShowDMP(consultation?.patient._id)

  const menuItems = useMemo(() => {
    const items = compact([
      {
        id: 'patientInfo',
        icon: PatientIdentityIcon,
        label: t('page.liveConsultation.sidebar.patientInfo.label'),
      },
      {
        id: 'mesures',
        icon: VitalParametersIcon,
        label: t('page.liveConsultation.sidebar.mesures.label'),
      },
      {
        id: 'documents',
        icon: ConsultationDocumentsIcon,
        label: t('page.liveConsultation.sidebar.documents.label'),
      },
      {
        id: 'payment',
        icon: PaymentIcon,
        label: t('page.liveConsultation.sidebar.payment.label'),
      },
      {
        id: 'transcriptions',
        icon: RecordVoiceOverIcon,
        label: 'transcriptions',
      },
      {
        id: 'forms',
        icon: AssignmentIcon,
        label: t('page.localConsultation.sidebar.forms.label'),
      },
      {
        id: 'medicalActs',
        icon: LocalHospitalIcon,
        label: t('page.liveConsultation.sidebar.medicalActs.label', { defaultValue: 'Medical Acts' }),
      },
      showDMP && {
        id: 'dmp',
        icon: FolderSharedIcon,
        label: t('page.liveConsultation.sidebar.dmp.label', { defaultValue: 'DMP' }),
      },
      showDMP && {
        id: 'mss',
        icon: EmailIcon,
        label: 'MS Santé',
      },
      {
        id: 'mesures',
        icon: VitalParametersIcon,
        label: t('page.localConsultation.sidebar.mesures.label'),
        isMenuDiagnostic: true,
      },
      {
        id: 'medicalVideo',
        icon: MedicalVideoIcon,
        label: t('page.liveConsultation.sidebar.medicalVideo.label'),
        isMenuDiagnostic: true,
      },
      {
        id: 'biologicals',
        icon: Biotech,
        label: t('page.localConsultation.sidebar.biologicals.label'),
        isMenuDiagnostic: true,
      },
      {
        id: 'stethoscope',
        icon: StethoscopeIcon,
        label: t('page.localConsultation.sidebar.stethoscope.label'),
        isNested: isTooSmall,
        isMenuDiagnostic: true,
      },
    ])

    const filteredItems = items.filter((item) => {
      if (item.id === 'medicalActs') {
        return hasMedicalActsLicense
      }
      return true
    })

    return filteredItems
  }, [t, hasMedicalActsLicense, isTooSmall, showDMP])

  const [selectedMenuItem, setSelectedMenuItem] = useState<string>(() => {
    const menuItemParam = searchParams.get('menu_item')
    return (menuItems.find((menuItem) => menuItem.id === menuItemParam) || menuItems[0]).id
  })

  return {
    menuItems,
    selectedMenuItem,
    setSelectedMenuItem,
  }
}
